import { Task, CalendarEvent, Habit } from '../contexts/AppContext';
import { getAIPreferences } from '../services/trainingService';

/**
 * Parse time string (e.g., "2:00 PM", "14:00") to minutes since midnight
 */
export function parseTimeToMinutes(timeStr: string): number {
    if (!timeStr) return 0;

    // Handle "HH:MM" format
    const militaryMatch = timeStr.match(/^(\d{1,2}):(\d{2})$/);
    if (militaryMatch) {
        return parseInt(militaryMatch[1]) * 60 + parseInt(militaryMatch[2]);
    }

    // Handle "H:MM AM/PM" format
    const amPmMatch = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (amPmMatch) {
        let hours = parseInt(amPmMatch[1]);
        const minutes = parseInt(amPmMatch[2]);
        const isPM = amPmMatch[3].toUpperCase() === 'PM';

        if (isPM && hours !== 12) hours += 12;
        if (!isPM && hours === 12) hours = 0;

        return hours * 60 + minutes;
    }

    return 0;
}

/**
 * Calculate duration between two time strings in minutes
 */
export function calculateDuration(timeStart: string, timeEnd: string): number {
    const start = parseTimeToMinutes(timeStart);
    const end = parseTimeToMinutes(timeEnd);

    if (end <= start) return 0; // Invalid or overnight
    return end - start;
}

/**
 * Context needed for workload calculation
 */
export interface WorkloadContext {
    tasks: Task[];
    events: CalendarEvent[];
    currentDate: string; // YYYY-MM-DD
}

/**
 * Calculate workload score for the day (0-1)
 * 1.0 = fully booked, 0.0 = completely free
 * 
 * @param ctx - Current tasks and events for the day
 * @returns number between 0 and 1
 */
export function calculateWorkloadScore(ctx: WorkloadContext): number {
    const prefs = getAIPreferences();

    // Get available work hours
    const workStart = parseTimeToMinutes(prefs.workStartTime);
    const workEnd = parseTimeToMinutes(prefs.workEndTime);
    const availableMinutes = workEnd - workStart;

    if (availableMinutes <= 0) return 0;

    let scheduledMinutes = 0;

    // Calculate task time for today
    const todayTasks = ctx.tasks.filter(t =>
        !t.completed &&
        !t.deletedAt &&
        t.dueDate === ctx.currentDate &&
        t.timeStart &&
        t.timeEnd
    );

    for (const task of todayTasks) {
        scheduledMinutes += calculateDuration(task.timeStart!, task.timeEnd!);
    }

    // Calculate event time for today
    const todayEvents = ctx.events.filter(e =>
        !e.deletedAt &&
        e.date === ctx.currentDate
    );

    for (const event of todayEvents) {
        scheduledMinutes += calculateDuration(event.timeStart, event.timeEnd);
    }

    // Calculate score (capped at 1.0)
    const score = Math.min(scheduledMinutes / availableMinutes, 1.0);

    return Math.round(score * 100) / 100; // Round to 2 decimal places
}

/**
 * Check if a habit is due today based on its frequency
 */
export function isHabitDueToday(habit: Habit, today: string): boolean {
    if (habit.deletedAt) return false;

    const date = new Date(today + 'T00:00:00');
    const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.

    switch (habit.frequency) {
        case 'daily':
            return true;
        case 'weekdays':
            return dayOfWeek >= 1 && dayOfWeek <= 5; // Monday-Friday
        case 'weekly':
            // Weekly habits are due on the day they were created
            const createdDate = new Date(habit.createdAt);
            return createdDate.getDay() === dayOfWeek;
        case 'custom':
            // Check if today is in customDays
            return habit.customDays?.includes(dayOfWeek) ?? false;
        default:
            return false;
    }
}

/**
 * Get incomplete habits that are due today
 */
export function getHabitGaps(habits: Habit[], today: string): Habit[] {
    return habits.filter(habit => {
        if (!isHabitDueToday(habit, today)) return false;
        // Check if not completed today
        return !habit.completedDates.includes(today);
    });
}

/**
 * Get high-priority tasks from yesterday that weren't completed
 */
export function getOverdueHighPriorityTasks(tasks: Task[], today: string): Task[] {
    const todayDate = new Date(today + 'T00:00:00');

    return tasks.filter(task => {
        if (task.completed || task.deletedAt) return false;
        if (task.priority !== 'high') return false;
        if (!task.dueDate) return false;

        const taskDate = new Date(task.dueDate + 'T00:00:00');
        return taskDate < todayDate;
    });
}

/**
 * Check if a task or event is VIP-protected (cannot be auto-rescheduled)
 * VIP entities are names of people or projects the user never wants rescheduled
 * @param title - The title of the task/event to check
 * @param vipEntities - List of VIP entity names
 */
export function isVIPProtected(title: string, vipEntities: string[]): boolean {
    if (!vipEntities || vipEntities.length === 0) return false;
    const titleLower = title.toLowerCase();
    return vipEntities.some(vip =>
        titleLower.includes(vip.toLowerCase())
    );
}

/**
 * Find available time slots in today's schedule
 * Returns array of { start: string, end: string, duration: number } objects
 */
export function findAvailableSlots(
    tasks: Task[],
    events: CalendarEvent[],
    today: string
): Array<{ start: string; end: string; durationMinutes: number }> {
    const prefs = getAIPreferences();
    const workStart = parseTimeToMinutes(prefs.workStartTime);
    const workEnd = parseTimeToMinutes(prefs.workEndTime);

    // Collect all scheduled blocks for today
    const blocks: Array<{ start: number; end: number }> = [];

    // Add tasks with times
    tasks
        .filter(t => !t.completed && !t.deletedAt && t.dueDate === today && t.timeStart && t.timeEnd)
        .forEach(t => {
            blocks.push({
                start: parseTimeToMinutes(t.timeStart!),
                end: parseTimeToMinutes(t.timeEnd!)
            });
        });

    // Add events
    events
        .filter(e => !e.deletedAt && e.date === today)
        .forEach(e => {
            blocks.push({
                start: parseTimeToMinutes(e.timeStart),
                end: parseTimeToMinutes(e.timeEnd)
            });
        });

    // Sort by start time
    blocks.sort((a, b) => a.start - b.start);

    // Find gaps
    const slots: Array<{ start: string; end: string; durationMinutes: number }> = [];
    let currentTime = workStart;

    for (const block of blocks) {
        if (block.start > currentTime) {
            const duration = block.start - currentTime;
            if (duration >= 30) { // Only count slots of 30+ minutes
                slots.push({
                    start: minutesToTimeString(currentTime),
                    end: minutesToTimeString(block.start),
                    durationMinutes: duration
                });
            }
        }
        currentTime = Math.max(currentTime, block.end);
    }

    // Check for slot at end of day
    if (workEnd > currentTime) {
        const duration = workEnd - currentTime;
        if (duration >= 30) {
            slots.push({
                start: minutesToTimeString(currentTime),
                end: minutesToTimeString(workEnd),
                durationMinutes: duration
            });
        }
    }

    return slots;
}

/**
 * Convert minutes since midnight to time string (e.g., "2:30 PM")
 */
function minutesToTimeString(minutes: number): string {
    const hours24 = Math.floor(minutes / 60);
    const mins = minutes % 60;

    const hours12 = hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24;
    const amPm = hours24 >= 12 ? 'PM' : 'AM';

    return `${hours12}:${mins.toString().padStart(2, '0')} ${amPm}`;
}
